import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Table,
  Stack,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TablePagination,
  TextField,
  InputAdornment,
  CircularProgress,
  Box,
  TableHead,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';
import { useUsers } from '../hooks/useUsers';
import AddTicketsDialog from '../components/users/AddTicketsDialog';

export default function UsersPage() {
  const navigate = useNavigate();
  const { users, totalUsers, loading, error, fetchUsers, addTickets, fixUserLeagues } = useUsers();
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [fixingUserLeagues, setFixingUserLeagues] = useState(false);
  const [fixUserLeaguesSuccess, setFixUserLeaguesSuccess] = useState(null);
  const [fixUserLeaguesError, setFixUserLeaguesError] = useState(null);
  const [confirmFixDialogOpen, setConfirmFixDialogOpen] = useState(false);

  const loadUsers = async () => {
    try {
      await fetchUsers({
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        search: searchQuery,
      });
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };

  // Fetch users on mount and when pagination/search changes
  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, searchQuery]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchInput(event.target.value);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setSearchQuery(searchInput);
    setPage(0);
  };

  const handleOpenAddTickets = (user) => {
    setSelectedUser(user);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
  };

  const handleAddTickets = async (userId, amount) => {
    await addTickets(userId, amount);
    // Refresh the users list after adding tickets
    await loadUsers();
  };

  const handleFixUserLeagues = async () => {
    setFixingUserLeagues(true);
    setFixUserLeaguesSuccess(null);
    setFixUserLeaguesError(null);
    try {
      const response = await fixUserLeagues();
      setFixUserLeaguesSuccess(
        `User leagues fixed successfully. Processed users: ${response?.processed_users ?? 0}`
      );
    } catch (err) {
      setFixUserLeaguesError(
        err?.response?.data?.error || err?.message || 'Failed to fix user leagues'
      );
    } finally {
      setFixingUserLeagues(false);
    }
  };

  const handleConfirmFixUserLeagues = () => {
    setConfirmFixDialogOpen(false);
    handleFixUserLeagues();
  };

  return (
    <>
      <Helmet>
        <title>Users | Admin Panel</title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Users
          </Typography>
          <Button
            variant="contained"
            color="primary"
            endIcon={
              fixingUserLeagues ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Iconify icon="eva:refresh-fill" />
              )
            }
            onClick={() => setConfirmFixDialogOpen(true)}
            disabled={fixingUserLeagues}
          >
            Fix User Leagues
          </Button>
        </Stack>

        {fixUserLeaguesSuccess && (
          <Box sx={{ mb: 3 }}>
            <Alert severity="success">{fixUserLeaguesSuccess}</Alert>
          </Box>
        )}

        {fixUserLeaguesError && (
          <Box sx={{ mb: 3 }}>
            <Alert severity="error">{fixUserLeaguesError}</Alert>
          </Box>
        )}

        <Card>
          <Box sx={{ p: 3 }}>
            <form onSubmit={handleSearchSubmit}>
              <TextField
                fullWidth
                placeholder="Search by username, SSO user ID, or service user ID..."
                value={searchInput}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchInput && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          setSearchInput('');
                          setSearchQuery('');
                          setPage(0);
                        }}
                        edge="end"
                      >
                        <Iconify icon="eva:close-fill" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </form>
          </Box>

          {error && (
            <Box sx={{ px: 3, pb: 2 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User ID</TableCell>
                    <TableCell>Username</TableCell>
                    <TableCell>SSO User ID</TableCell>
                    <TableCell>Service User ID</TableCell>
                    <TableCell align="center">Tickets</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 10 }}>
                        <Typography variant="body2" color="text.secondary">
                          {searchQuery ? 'No users found' : 'No users available'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((user) => (
                      <TableRow hover key={user.user_id}>
                        <TableCell>{user.user_id}</TableCell>
                        <TableCell>
                          <Typography
                            variant="subtitle2"
                            noWrap
                            sx={{
                              cursor: 'pointer',
                              color: 'primary.main',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                            onClick={() => navigate(`/dashboard/user/${user.user_id}`)}
                          >
                            {user.username}
                          </Typography>
                        </TableCell>
                        <TableCell>{user.sso_user_id || '-'}</TableCell>
                        <TableCell>{user.service_user_id || '-'}</TableCell>
                        <TableCell align="center">
                          <Typography variant="subtitle2">{user.tickets}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<Iconify icon="eva:plus-fill" />}
                            onClick={() => handleOpenAddTickets(user)}
                          >
                            Add Tickets
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalUsers}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>

      <AddTicketsDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        user={selectedUser}
        onConfirm={handleAddTickets}
      />

      <Dialog
        open={confirmFixDialogOpen}
        onClose={() => setConfirmFixDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Fix User Leagues</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to run this action for all users?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmFixDialogOpen(false)} disabled={fixingUserLeagues}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmFixUserLeagues}
            variant="contained"
            color="primary"
            disabled={fixingUserLeagues}
            endIcon={fixingUserLeagues ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
